import { ImageStack } from '@creditornot/cb-icons';
import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import React, { Children } from 'react';
import styled, { css, keyframes } from 'styled-components';

import LocalizedNumber from 'common/components/LocalizedNumber';
import { Typography as T } from 'common/typography';
import { solidify } from 'modules/menu/solidify';

type Props = {
  children: React.ReactNode;
  className?: string;
  isExpanded?: boolean;
  totalImagesIndicator: number;
};

const thumbnailSize = '56px';
const thumbnailMargin = '8px';

const expandedStyle = css<{ totalChildren: number }>`
  width: ${({ totalChildren }) =>
    `calc((${thumbnailSize} + ${thumbnailMargin}) * ${totalChildren} + (${thumbnailMargin} * 2))`};
  & > * {
    margin-inline-end: ${thumbnailMargin};
  }
`;

const TotalImagesIndicatorAnimation = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, 0);
  }

  to {
    opacity: 1;
    transform: translate(-50%, 50%);
  }

  from, to {
    right: auto;
    margin-inline-start: 50%;
  }
`;

const ImageStackIcon = styled(ImageStack).attrs({ width: 12, height: 12 })`
  margin-inline-end: 4px;
  fill: ${cssThemedValue('surfaceMain')};
`;

const TotalImagesIndicator = styled(T.Small)`
  position: absolute;
  display: flex;
  align-items: center;
  bottom: 0;
  right: 0;
  padding: 2px 6px;
  border-radius: 16px;
  background-color: ${cssThemedValue('borderPrimary')};
  color: ${cssThemedValue('surfaceMain')};
  z-index: 2;
`;

const Container = styled.div`
  position: absolute;
  top: 0;
  padding-inline-end: calc(${thumbnailMargin} * 2);
  width: ${thumbnailSize};
  transition: width 200ms ease-in-out;
  display: inline-flex;
  flex-direction: row-reverse;
  justify-content: flex-end;

  & > * {
    margin-inline-end: -${thumbnailSize};
    transition: margin 200ms ease-in-out;
  }
`;

const Root = styled.div<{ isExpanded: boolean; totalChildren: number }>`
  position: relative;
  width: ${thumbnailSize};
  height: ${thumbnailSize};
  cursor: default;

  &:hover > ${Container} {
    ${expandedStyle}
  }

  &:hover > ${TotalImagesIndicator} {
    border: 1px solid ${cssThemedValue('borderPrimary')};
    background-color: ${cssThemedValue({
      light: solidify(colors.pepper8, 'light'),
      dark: solidify(colors.salt8, 'dark'),
    })};
    color: ${cssThemedValue('textSecondary')};
    animation: ${TotalImagesIndicatorAnimation} 200ms ease-in-out forwards;

    ${ImageStackIcon} {
      fill: ${cssThemedValue('textSecondary')};
    }
  }

  ${({ isExpanded }) =>
    isExpanded &&
    css`
      & > ${TotalImagesIndicator} {
        opacity: 0;
      }
      & > ${Container} {
        ${expandedStyle}
      }
    `}
`;

const CollapsibleThumbnailsContainer = ({ children, className, isExpanded = false, totalImagesIndicator }: Props) => {
  const totalChildren = Children.count(children);

  return (
    <Root className={className} isExpanded={isExpanded} totalChildren={totalChildren}>
      <Container>{children}</Container>
      {totalImagesIndicator > 1 && (
        <TotalImagesIndicator>
          <ImageStackIcon />
          <LocalizedNumber value={totalImagesIndicator} />
        </TotalImagesIndicator>
      )}
    </Root>
  );
};

export default CollapsibleThumbnailsContainer;
